import { useCallback, useState } from 'react';

import type { Environment } from '@change-corgi/core/environment';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import type { SandboxContract } from '#src/app/pages/startAPetitionV2/shared/components';
import { isDarkModeApplied } from '#src/app/shared/utils/dom';

import { useChatAgentSubmit } from '../useChatAgentSubmit';

type SandboxErrorParams = Parameters<SandboxContract['actions']['error']>[0];

type UseChatAgentReturn = ModelHookResult<
	{ iframeSrc: string; loading: boolean; error: unknown },
	{
		onSubmit: ReturnType<typeof useChatAgentSubmit>['actions']['onSubmit'];
		onLoadTimeout: () => void;
		onError: (params: SandboxErrorParams) => void;
	}
>;

/** Currently, only production vs. non-production matters for the chat agent's own deployment. */
function getChildOrigin(environment: Environment): string {
	if (environment === 'development') return 'https://petition-chat.localhost';
	if (environment === 'production') return 'https://chat-agent.change.org';
	return 'https://sap-4-prototype-2-git-staging-change-org-team.vercel.app';
}

/**
 * Codes the child currently defines (its own error-reporting design owns this set, not us) —
 * bounded here so `error_type` stays a small, known set of values rather than an arbitrary,
 * child-controlled string.
 */
const KNOWN_CHAT_ERROR_CODES = ['chat_message_failed', 'angle_generation_failed', 'draft_extraction_failed'] as const;

export function useChatAgent(): UseChatAgentReturn {
	const { environment } = useUtilityContext();
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	// Captured once on mount: `data.draft` can keep syncing while the chat is open (e.g. the sandbox's
	// `update` messages), and recomputing `src` from live draft state would change the iframe's `src`
	// prop, which makes React reload the document and drop the in-progress conversation.
	const [iframeSrc] = useState(() => {
		const childOrigin = getChildOrigin(environment.getEnvironment());
		const url = new URL('/chat', childOrigin);

		const issuePrompt = data.draft?.input?.issuePrompt;
		if (issuePrompt) url.searchParams.set('initial', issuePrompt);

		url.searchParams.set('theme', isDarkModeApplied() ? 'dark' : 'light');

		return url.toString();
	});

	const { data: submitData, actions: submitActions } = useChatAgentSubmit();

	// the sandbox never handshaked `ready` — raise a `load_timeout` error so the machine
	// routes the user out of the (never-shown) chat; see chatAgent/actor.ts `intro`.
	const onLoadTimeout = useCallback(() => {
		actions.send({ type: 'error', reason: 'load_timeout' });
	}, [actions]);

	// A failure the chat child reported up its own channel. The sandbox already validated and
	// reported it to Sentry; here we only track volume (most of these codes are `recoverable`
	// with the child's own retry, so a Sentry report per occurrence would just be noise on top
	// of the one the sandbox already sends) and pick the recovery. Per-`code` cases get added as
	// the child defines error codes — until then every code hits the conservative fallback below.
	const onError = useCallback(
		({ code, recoverable }: SandboxErrorParams) => {
			void track('sap_chat_agent_error', {
				error_type: (KNOWN_CHAT_ERROR_CODES as readonly string[]).includes(code) ? code : 'unknown',
				error_variant: recoverable ? 'recoverable' : 'unrecoverable',
			});

			switch (code) {
				// case '<code>': <side effect that should override the generic recoverable/unrecoverable handling below>; break;
				default:
					// unrecoverable (or the child didn't say): drop to the manual create flow so the
					// user can still make a petition. Recoverable codes are a no-op here — the child
					// already retries and shows its own UI for those.
					if (!recoverable) actions.send({ type: 'error' });
			}
		},
		[actions, track],
	);

	return {
		data: { iframeSrc, loading: submitData.loading, error: submitData.error },
		actions: { onSubmit: submitActions.onSubmit, onLoadTimeout, onError },
	};
}
